import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { unsubscribeNewsletter } from "@/lib/bookings.functions";

const title = "Unsubscribe — Car Rental Bucharest";
const description =
  "Unsubscribe from the Car Rental Bucharest newsletter in one click. Your booking confirmations and reservation emails are never affected by this change.";

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const search = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const [email, setEmail] = useState(search?.get("email") ?? "");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [message, setMessage] = useState("");
  const run = useServerFn(unsubscribeNewsletter);

  return (
    <div className="container-page max-w-xl py-20">
      <p className="eyebrow">Newsletter</p>
      <h1 className="mt-3 text-3xl font-bold sm:text-4xl">Unsubscribe</h1>
      <p className="mt-4 text-muted-foreground">
        Enter the email address you subscribed with. You will stop receiving newsletter emails.
        Booking and reservation emails are not affected.
      </p>

      {state === "done" ? (
        <p className="mt-8 rounded-xl border border-border bg-card p-5 text-sm">
          You have been unsubscribed. Sorry to see you go.
        </p>
      ) : (
        <form
          className="mt-8 flex flex-col gap-3 sm:flex-row"
          onSubmit={async (event) => {
            event.preventDefault();
            setState("loading");
            try {
              await run({ data: { email } });
              setState("done");
            } catch (err) {
              setState("error");
              setMessage(err instanceof Error ? err.message : "Something went wrong.");
            }
          }}
        >
          <Input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            aria-label="Email address"
          />
          <Button type="submit" disabled={state === "loading"}>
            {state === "loading" ? "Working…" : "Unsubscribe"}
          </Button>
        </form>
      )}
      {state === "error" ? <p className="mt-4 text-sm text-destructive">{message}</p> : null}
    </div>
  );
}
